import { useQuery } from '@tanstack/react-query';
import { getQueryFn } from '@/lib/queryClient';
import { deriveAuthResolution, type AuthResolution } from '@/lib/authResolution';
import { useTestingMode } from './useTestingMode';

export interface AuthUser {
  id: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  userType?:
    'patient' | 'doctor' | 'hospital' | 'pharmacy' | 'lab' | 'health_services' | 'system_admin';
  specialization?: string;
  role?: string;
  profileImageUrl?: string;
}

// Helper to get display name from user
export function getUserDisplayName(user: AuthUser | null): string {
  if (!user) return '';
  if (user.name) return user.name;
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
  if (user.firstName) return user.firstName;
  return user.email || '';
}

export function useAuth() {
  const { isTestingMode, getTestUser, currentTestRole } = useTestingMode();

  const {
    data: user,
    isLoading,
    error,
    status,
    fetchStatus,
    refetch,
  } = useQuery<AuthUser | null>({
    queryKey: ['/api/auth/user'],
    // 401 = logged out, an expected state. Map it to null (a cacheable
    // success) instead of the global on401:'throw' default, which leaves the
    // query permanently errored+stale and refetched by every consumer.
    queryFn: getQueryFn<AuthUser | null>({ on401: 'returnNull' }),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    enabled: !isTestingMode, // Disable real auth query in testing mode
  });

  // `retryAuth` always returns void — callers (AuthUnavailableFallback) don't
  // need the refetch result, only a way to trigger one.
  const retryAuth = (): void => {
    void refetch();
  };

  // Return testing user when in testing mode.
  //
  // `resolution` / `retryAuth` are returned here too (plan risk R-5): once
  // `resolution` gates ~35 routes via `GatedRoute` (Phase 3) rather than one
  // terminal route, a missing field on THIS branch degrades every gated page
  // to the signed-out fallback under testing mode — a visible failure, not a
  // leak, because `GatedRoute`'s `default` case fails closed. Still a real
  // regression if it went unassigned, so it is assigned explicitly rather
  // than left to fall through as `undefined`.
  if (isTestingMode) {
    const testUser = getTestUser(currentTestRole);
    const testResolution: AuthResolution = testUser ? 'authenticated' : 'refused';
    return {
      user: testUser,
      isLoading: false,
      isAuthenticated: !!testUser,
      error: null,
      resolution: testResolution,
      retryAuth: (): void => undefined,
    };
  }

  const resolution = deriveAuthResolution({ status, fetchStatus, data: user });

  return {
    user,
    isLoading,
    isAuthenticated: !!user && !error,
    error,
    resolution,
    retryAuth,
  };
}
